export interface PriceEntry {
  id: string;
  propertyId: string;
  roomTypeId: string;
  date: string;
  price: number;
  currency: string;
  originalPrice?: number;
  adjustmentReason?: string;
  createdBy: string;
  createdAt: Date;
  updatedBy?: string;
  updatedAt?: Date;
  version: number;
}

export interface PriceBatchUpdate {
  entries: Omit<PriceEntry, 'id' | 'createdAt' | 'updatedAt' | 'version'>[];
  batchId: string;
  totalEntries: number;
}

export interface PriceValidationError {
  field: string;
  value: unknown;
  error: string;
  code: string;
}

import { supabase } from './supabaseClient';
import { log } from '../utils/logger';
import { standardizeSupabaseError, logError } from '../utils/errorHandling';

export class PriceStorageService {
  private prices: Map<string, PriceEntry> = new Map();
  private readonly STORAGE_KEY = 'smartrate-prices';
  private readonly BACKUP_KEY = 'smartrate-prices-backup';
  private pricesByDate: Map<string, Set<string>> = new Map();
  private pricesByRoom: Map<string, Set<string>> = new Map();

  constructor() {
    this.loadFromStorage();
    this.setupAutoBackup();
  }

  // 1. PRICE VALIDATION
  validatePrice(
    price: unknown,
    currency: string = 'USD'
  ): PriceValidationError[] {
    const errors: PriceValidationError[] = [];

    // Check if price exists
    if (price === null || price === undefined || price === '') {
      errors.push({
        field: 'price',
        value: price,
        error: 'Price is required',
        code: 'PRICE_REQUIRED',
      });
      return errors;
    }

    // Convert to number if string
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;

    // Check if valid number
    if (isNaN(numericPrice) || !isFinite(numericPrice)) {
      errors.push({
        field: 'price',
        value: price,
        error: 'Price must be a valid number',
        code: 'INVALID_NUMBER',
      });
      return errors;
    }

    // Check if positive
    if (numericPrice < 0) {
      errors.push({
        field: 'price',
        value: price,
        error: 'Price must be positive',
        code: 'NEGATIVE_PRICE',
      });
    }

    // Check decimal places (max 2 for most currencies)
    const decimalPlaces = this.getDecimalPlaces(numericPrice);
    const maxDecimals = this.getMaxDecimalPlaces(currency);
    if (decimalPlaces > maxDecimals) {
      errors.push({
        field: 'price',
        value: price,
        error: `Price can have maximum ${maxDecimals} decimal places for ${currency}`,
        code: 'INVALID_DECIMALS',
      });
    }

    // Check reasonable price range
    const { min, max } = this.getPriceRange(currency);
    if (numericPrice < min || numericPrice > max) {
      errors.push({
        field: 'price',
        value: price,
        error: `Price must be between ${min} and ${max} ${currency}`,
        code: 'PRICE_OUT_OF_RANGE',
      });
    }

    return errors;
  }

  private getDecimalPlaces(num: number): number {
    const str = num.toString();
    if (str.indexOf('.') !== -1) {
      return str.split('.')[1].length;
    }
    return 0;
  }

  private getMaxDecimalPlaces(currency: string): number {
    // Most currencies use 2 decimal places, some use 0 (JPY, KRW), some use 3 (BHD, KWD)
    const noDecimalCurrencies = ['JPY', 'KRW', 'VND', 'CLP'];
    const threeDecimalCurrencies = ['BHD', 'KWD', 'OMR'];

    if (noDecimalCurrencies.includes(currency)) return 0;
    if (threeDecimalCurrencies.includes(currency)) return 3;
    return 2;
  }

  private getPriceRange(currency: string): { min: number; max: number } {
    // Define reasonable price ranges per currency
    const ranges: Record<string, { min: number; max: number }> = {
      USD: { min: 1, max: 50000 },
      EUR: { min: 1, max: 45000 },
      GBP: { min: 1, max: 40000 },
      JPY: { min: 100, max: 5000000 },
      CAD: { min: 1, max: 65000 },
      AUD: { min: 1, max: 70000 },
    };

    return ranges[currency] || { min: 1, max: 50000 };
  }

  // 2. SINGLE PRICE SAVE
  async savePrice(priceData: {
    propertyId: string;
    roomTypeId: string;
    date: string;
    price: number;
    currency?: string;
    createdBy: string;
    adjustmentReason?: string;
  }): Promise<{
    success: boolean;
    priceEntry?: PriceEntry;
    errors?: PriceValidationError[];
  }> {
    try {
      const currency = priceData.currency || 'USD';

      // Validate price
      const validationErrors = this.validatePrice(priceData.price, currency);
      if (validationErrors.length > 0) {
        return { success: false, errors: validationErrors };
      }

      // Validate required fields
      const requiredFields = ['propertyId', 'roomTypeId', 'date', 'createdBy'];
      for (const field of requiredFields) {
        if (!priceData[field as keyof typeof priceData]) {
          return {
            success: false,
            errors: [
              {
                field,
                value: priceData[field as keyof typeof priceData],
                error: `${field} is required`,
                code: 'FIELD_REQUIRED',
              },
            ],
          };
        }
      }

      // Validate date format
      const dateObj = new Date(priceData.date);
      if (isNaN(dateObj.getTime())) {
        return {
          success: false,
          errors: [
            {
              field: 'date',
              value: priceData.date,
              error: 'Invalid date format',
              code: 'INVALID_DATE',
            },
          ],
        };
      }

      // Create unique key
      const key = `${priceData.propertyId}-${priceData.roomTypeId}-${priceData.date}`;

      // Check if price already exists
      const existingPrice = this.prices.get(key);
      const now = new Date();

      const priceEntry: PriceEntry = {
        id:
          existingPrice?.id ||
          `price-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        propertyId: priceData.propertyId,
        roomTypeId: priceData.roomTypeId,
        date: priceData.date,
        price: Math.round(priceData.price * 100) / 100, // Round to 2 decimal places
        currency,
        originalPrice: existingPrice?.price,
        adjustmentReason: priceData.adjustmentReason,
        createdBy: existingPrice?.createdBy || priceData.createdBy,
        createdAt: existingPrice?.createdAt || now,
        updatedBy: existingPrice ? priceData.createdBy : undefined,
        updatedAt: existingPrice ? now : undefined,
        version: (existingPrice?.version || 0) + 1,
      };

      // Save to memory
      this.prices.set(key, priceEntry);

      // Persist to storage
      await this.persistToStorage();

      // Create backup
      await this.createBackup();

      // Save to Supabase
      await this.saveToSupabase(priceEntry);

      return { success: true, priceEntry };
    } catch (error) {
      log.errorWithStack(
        error instanceof Error
          ? error
          : new Error('Unknown error saving price'),
        'priceStorage',
        { operation: 'savePrice', priceData }
      );
      return {
        success: false,
        errors: [
          {
            field: 'system',
            value: null,
            error:
              error instanceof Error ? error.message : 'Unknown error occurred',
            code: 'SYSTEM_ERROR',
          },
        ],
      };
    }
  }

  // Save to Supabase database
  private async saveToSupabase(priceEntry: PriceEntry): Promise<void> {
    try {
      log.database('upsert', 'current_room_rates', {
        property_id: priceEntry.propertyId,
        room_type_id: priceEntry.roomTypeId,
        date: priceEntry.date,
        rate: priceEntry.price,
        currency: priceEntry.currency,
      });

      // First, update or insert into current_room_rates
      const { data: upsertData, error: rateError } = await supabase
        .from('current_room_rates')
        .upsert(
          {
            property_id: priceEntry.propertyId,
            room_type_id: priceEntry.roomTypeId,
            date: priceEntry.date,
            rate: priceEntry.price,
            currency: priceEntry.currency,
            last_updated: new Date().toISOString(),
            updated_by: priceEntry.updatedBy || priceEntry.createdBy,
          },
          {
            onConflict: 'property_id,room_type_id,date,rate_plan',
          }
        )
        .select();

      if (rateError) {
        const standardError = standardizeSupabaseError(rateError);
        logError(standardError, {
          operation: 'updateCurrentRoomRates',
          additionalData: { priceEntry },
        });
        throw rateError;
      }

      log.database('upsert_success', 'current_room_rates', {
        recordCount: upsertData?.length || 0,
      });

      // Then, log the price change
      if (
        priceEntry.originalPrice !== undefined &&
        priceEntry.originalPrice !== priceEntry.price
      ) {
        const { data: logData, error: priceLogError } = await supabase
          .from('price_logs')
          .insert({
            property_id: priceEntry.propertyId,
            room_type_id: priceEntry.roomTypeId,
            date: priceEntry.date,
            old_price: priceEntry.originalPrice,
            new_price: priceEntry.price,
            rule_triggered:
              (priceEntry as PriceEntry & { ruleTriggered?: string })
                .ruleTriggered || 'manual',
            change_reason: priceEntry.adjustmentReason || 'Manual update',
            occupancy_at_change:
              (priceEntry as PriceEntry & { occupancyAtChange?: number })
                .occupancyAtChange || 0,
            booking_window_days:
              (priceEntry as PriceEntry & { bookingWindowDays?: number })
                .bookingWindowDays || 0,
            cancellation_related:
              (priceEntry as PriceEntry & { cancellationRelated?: boolean })
                .cancellationRelated || false,
            cancellation_id:
              (priceEntry as PriceEntry & { cancellationId?: string })
                .cancellationId || null,
            gap_fill_related:
              (priceEntry as PriceEntry & { gapFillRelated?: boolean })
                .gapFillRelated || false,
            gap_id:
              (priceEntry as PriceEntry & { gapId?: string }).gapId || null,
            created_by: priceEntry.createdBy,
          })
          .select();

        if (priceLogError) {
          const standardError = standardizeSupabaseError(priceLogError);
          logError(standardError, {
            operation: 'logPriceChange',
            additionalData: { priceEntry },
          });
          throw priceLogError;
        }

        log.database('insert_success', 'price_logs', {
          recordCount: logData?.length || 0,
        });
      }

      // Verify the data was actually saved
      const { data: verifyData, error: verifyError } = await supabase
        .from('current_room_rates')
        .select('*')
        .eq('property_id', priceEntry.propertyId)
        .eq('room_type_id', priceEntry.roomTypeId)
        .eq('date', priceEntry.date)
        .single();

      if (verifyError) {
        const standardError = standardizeSupabaseError(verifyError);
        logError(standardError, {
          operation: 'verifyPriceSave',
          additionalData: { priceEntry },
        });
        throw new Error('Failed to verify saved data');
      }

      if (!verifyData) {
        throw new Error('Data was not saved to database');
      }

      log.database('verify_success', 'current_room_rates', {
        verifiedEntry: !!verifyData,
      });
    } catch (error) {
      log.errorWithStack(
        error instanceof Error
          ? error
          : new Error('Failed to save to Supabase'),
        'priceStorage',
        { operation: 'saveToSupabase', priceEntry }
      );
      throw error;
    }
  }

  // 3. BATCH PRICE SAVE
  async savePricesBatch(
    prices: Array<{
      propertyId: string;
      roomTypeId: string;
      date: string;
      price: number;
      currency?: string;
      createdBy: string;
      adjustmentReason?: string;
    }>,
    options: {
      validateAll?: boolean;
      stopOnError?: boolean;
      batchSize?: number;
    } = {}
  ): Promise<{
    success: boolean;
    batchId: string;
    results: Array<{
      index: number;
      success: boolean;
      priceEntry?: PriceEntry;
      errors?: PriceValidationError[];
    }>;
    summary: {
      total: number;
      successful: number;
      failed: number;
    };
  }> {
    const batchId = `batch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const results: Array<{
      index: number;
      success: boolean;
      priceEntry?: PriceEntry;
      errors?: PriceValidationError[];
    }> = [];

    const {
      validateAll = true,
      stopOnError = false,
      batchSize = 100,
    } = options;

    try {
      // Validate all entries first if requested
      if (validateAll) {
        for (let i = 0; i < prices.length; i++) {
          const priceData = prices[i];
          const validationErrors = this.validatePrice(
            priceData.price,
            priceData.currency || 'USD'
          );

          if (validationErrors.length > 0) {
            results.push({
              index: i,
              success: false,
              errors: validationErrors,
            });

            if (stopOnError) {
              return {
                success: false,
                batchId,
                results,
                summary: {
                  total: prices.length,
                  successful: 0,
                  failed: results.length,
                },
              };
            }
          }
        }
      }

      // Process in batches
      for (let i = 0; i < prices.length; i += batchSize) {
        const batch = prices.slice(i, i + batchSize);

        for (let j = 0; j < batch.length; j++) {
          const index = i + j;
          const priceData = batch[j];

          // Skip if already failed validation
          if (results.find((r) => r.index === index && !r.success)) {
            continue;
          }

          const result = await this.savePrice(priceData);
          results.push({
            index,
            success: result.success,
            priceEntry: result.priceEntry,
            errors: result.errors,
          });

          if (!result.success && stopOnError) {
            break;
          }
        }

        // Add small delay between batches to prevent overwhelming the system
        if (i + batchSize < prices.length) {
          await new Promise((resolve) => setTimeout(resolve, 10));
        }
      }

      const successful = results.filter((r) => r.success).length;
      const failed = results.filter((r) => !r.success).length;

      return {
        success: failed === 0,
        batchId,
        results,
        summary: {
          total: prices.length,
          successful,
          failed,
        },
      };
    } catch (error) {
      log.errorWithStack(
        error instanceof Error ? error : new Error('Error in batch save'),
        'priceStorage',
        { operation: 'savePricesBatch', batchSize: prices.length }
      );
      return {
        success: false,
        batchId,
        results,
        summary: {
          total: prices.length,
          successful: 0,
          failed: prices.length,
        },
      };
    }
  }

  // 4. PRICE RETRIEVAL
  async getPrice(
    propertyId: string,
    roomTypeId: string,
    date: string
  ): Promise<PriceEntry | null> {
    // First check local cache
    const key = `${propertyId}-${roomTypeId}-${date}`;
    const cachedPrice = this.prices.get(key);

    if (cachedPrice) {
      return cachedPrice;
    }

    // If not in cache, try to get from Supabase
    try {
      const { data, error } = await supabase
        .from('current_room_rates')
        .select('*')
        .eq('property_id', propertyId)
        .eq('room_type_id', roomTypeId)
        .eq('date', date)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Not found
          return null;
        }
        throw error;
      }

      if (data) {
        // Convert to PriceEntry format and cache it
        const priceEntry: PriceEntry = {
          id: `price-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          propertyId: data.property_id,
          roomTypeId: data.room_type_id,
          date: data.date,
          price: data.rate,
          currency: data.currency,
          createdBy: data.updated_by,
          createdAt: new Date(data.last_updated),
          version: 1,
        };

        this.prices.set(key, priceEntry);
        return priceEntry;
      }

      return null;
    } catch (error) {
      log.errorWithStack(
        error instanceof Error
          ? error
          : new Error('Error fetching price from Supabase'),
        'priceStorage',
        { operation: 'getPrice', propertyId, roomTypeId, date }
      );
      return null;
    }
  }

  async getPrices(filters: {
    propertyId?: string;
    roomTypeId?: string;
    dateRange?: { start: string; end: string };
    currency?: string;
  }): Promise<PriceEntry[]> {
    try {
      // Build Supabase query
      let query = supabase.from('current_room_rates').select('*');

      if (filters.propertyId) {
        query = query.eq('property_id', filters.propertyId);
      }

      if (filters.roomTypeId) {
        query = query.eq('room_type_id', filters.roomTypeId);
      }

      if (filters.currency) {
        query = query.eq('currency', filters.currency);
      }

      if (filters.dateRange) {
        query = query
          .gte('date', filters.dateRange.start)
          .lte('date', filters.dateRange.end);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      // Convert to PriceEntry format
      const priceEntries: PriceEntry[] = (data || []).map(
        (item: {
          property_id: string;
          room_type_id: string;
          date: string;
          rate: number;
          currency: string;
          updated_by: string;
          last_updated: string;
        }) => {
          const key = `${item.property_id}-${item.room_type_id}-${item.date}`;
          const entry: PriceEntry = {
            id: `price-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            propertyId: item.property_id,
            roomTypeId: item.room_type_id,
            date: item.date,
            price: item.rate,
            currency: item.currency,
            createdBy: item.updated_by,
            createdAt: new Date(item.last_updated),
            version: 1,
          };

          // Update local cache
          this.prices.set(key, entry);

          return entry;
        }
      );

      return priceEntries;
    } catch (error) {
      log.errorWithStack(
        error instanceof Error
          ? error
          : new Error('Error fetching prices from Supabase'),
        'priceStorage',
        { operation: 'getPrices', filters }
      );

      // Fall back to local cache if Supabase query fails
      let filteredPrices = Array.from(this.prices.values());

      if (filters.propertyId) {
        filteredPrices = filteredPrices.filter(
          (p) => p.propertyId === filters.propertyId
        );
      }

      if (filters.roomTypeId) {
        filteredPrices = filteredPrices.filter(
          (p) => p.roomTypeId === filters.roomTypeId
        );
      }

      if (filters.currency) {
        filteredPrices = filteredPrices.filter(
          (p) => p.currency === filters.currency
        );
      }

      if (filters.dateRange) {
        const startDate = new Date(filters.dateRange.start);
        const endDate = new Date(filters.dateRange.end);

        filteredPrices = filteredPrices.filter((p) => {
          const priceDate = new Date(p.date);
          return priceDate >= startDate && priceDate <= endDate;
        });
      }

      return filteredPrices.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
    }
  }

  // 5. STORAGE PERSISTENCE
  private async persistToStorage(): Promise<void> {
    try {
      // Limit storage to prevent quota issues
      // Keep most recent 1000 entries for main storage (more than backup)
      const MAX_STORAGE_ENTRIES = 1000;

      const entries = Array.from(this.prices.entries());

      // Sort by updatedAt/createdAt (most recent first) and limit
      const sortedEntries = entries
        .sort((a, b) => {
          const dateA = a[1].updatedAt || a[1].createdAt;
          const dateB = b[1].updatedAt || b[1].createdAt;
          return dateB.getTime() - dateA.getTime();
        })
        .slice(0, MAX_STORAGE_ENTRIES);

      const data = {
        prices: sortedEntries,
        timestamp: new Date().toISOString(),
        version: '1.0',
        totalEntries: entries.length,
        storedEntries: sortedEntries.length,
      };

      const dataJson = JSON.stringify(data);

      // Check if data size is reasonable (< 4MB for main storage)
      if (dataJson.length > 4 * 1024 * 1024) {
        console.warn(
          `[priceStorage] Storage size too large (${(dataJson.length / 1024 / 1024).toFixed(2)}MB), reducing entries`
        );
        // Try with fewer entries
        const reducedEntries = sortedEntries.slice(0, 500);
        const reducedData = {
          prices: reducedEntries,
          timestamp: new Date().toISOString(),
          version: '1.0',
          totalEntries: entries.length,
          storedEntries: reducedEntries.length,
        };
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(reducedData));
        return;
      }

      // Clear old data first to free up space
      try {
        localStorage.removeItem(this.STORAGE_KEY);
      } catch {
        // Ignore errors when removing
      }

      localStorage.setItem(this.STORAGE_KEY, dataJson);
    } catch (error) {
      // Handle quota exceeded gracefully
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        console.warn(
          '[priceStorage] localStorage quota exceeded during persist, clearing old data'
        );
        try {
          localStorage.removeItem(this.STORAGE_KEY);
          localStorage.removeItem(this.BACKUP_KEY);
        } catch {
          // Ignore cleanup errors
        }
        // Don't throw - allow the app to continue without local storage
        return;
      }

      log.errorWithStack(
        error instanceof Error
          ? error
          : new Error('Failed to persist to storage'),
        'priceStorage',
        { operation: 'persistToStorage' }
      );
      throw new Error('Storage persistence failed');
    }
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored) as { prices?: [string, PriceEntry][] };

        if (data.prices && Array.isArray(data.prices)) {
          this.prices = new Map(
            data.prices.map(([key, value]: [string, PriceEntry]) => [
              key,
              {
                ...value,
                createdAt: new Date(value.createdAt),
                updatedAt: value.updatedAt
                  ? new Date(value.updatedAt)
                  : undefined,
              },
            ])
          );
        }
      }
    } catch (error) {
      log.errorWithStack(
        error instanceof Error
          ? error
          : new Error('Failed to load from storage'),
        'priceStorage',
        { operation: 'loadFromStorage' }
      );
      this.loadFromBackup();
    }
  }

  private async createBackup(): Promise<void> {
    try {
      // Only backup the most recent entries to avoid quota issues
      // Limit to last 500 entries (approximately 250KB of data)
      const MAX_BACKUP_ENTRIES = 500;

      const entries = Array.from(this.prices.entries());

      // Sort by updatedAt/createdAt (most recent first) and take only MAX_BACKUP_ENTRIES
      const sortedEntries = entries
        .sort((a, b) => {
          const dateA = a[1].updatedAt || a[1].createdAt;
          const dateB = b[1].updatedAt || b[1].createdAt;
          return dateB.getTime() - dateA.getTime();
        })
        .slice(0, MAX_BACKUP_ENTRIES);

      const backup = {
        prices: sortedEntries,
        timestamp: new Date().toISOString(),
        version: '1.0',
        totalEntries: entries.length,
        backupEntries: sortedEntries.length,
      };

      const backupJson = JSON.stringify(backup);

      // Check if backup size is reasonable (< 2MB)
      if (backupJson.length > 2 * 1024 * 1024) {
        console.warn(
          `[priceStorage] Backup size too large (${(backupJson.length / 1024 / 1024).toFixed(2)}MB), skipping localStorage backup`
        );
        return;
      }

      // Clear old backup first to free up space
      try {
        localStorage.removeItem(this.BACKUP_KEY);
      } catch {
        // Ignore errors when removing
      }

      localStorage.setItem(this.BACKUP_KEY, backupJson);
    } catch (error) {
      // Handle quota exceeded gracefully - don't break the app
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        console.warn(
          '[priceStorage] localStorage quota exceeded, clearing old data'
        );
        // Try to clear ALL localStorage and skip backup
        try {
          localStorage.removeItem(this.BACKUP_KEY);
          localStorage.removeItem(this.STORAGE_KEY);
        } catch {
          // Ignore cleanup errors
        }
        return;
      }

      log.errorWithStack(
        error instanceof Error ? error : new Error('Failed to create backup'),
        'priceStorage',
        { operation: 'createBackup' }
      );
    }
  }

  private loadFromBackup(): void {
    try {
      const backup = localStorage.getItem(this.BACKUP_KEY);
      if (backup) {
        const data = JSON.parse(backup) as { prices?: [string, PriceEntry][] };

        if (data.prices && Array.isArray(data.prices)) {
          this.prices = new Map(
            data.prices.map(([key, value]: [string, PriceEntry]) => [
              key,
              {
                ...value,
                createdAt: new Date(value.createdAt),
                updatedAt: value.updatedAt
                  ? new Date(value.updatedAt)
                  : undefined,
              },
            ])
          );
        }
      }
    } catch (error) {
      log.errorWithStack(
        error instanceof Error
          ? error
          : new Error('Failed to load from backup'),
        'priceStorage',
        { operation: 'loadFromBackup' }
      );
    }
  }

  private setupAutoBackup(): void {
    // Create backup every 5 minutes
    setInterval(
      () => {
        this.createBackup();
      },
      5 * 60 * 1000
    );
  }

  // 6. DATA INTEGRITY CHECKS
  async verifyDataIntegrity(): Promise<{
    isValid: boolean;
    issues: Array<{
      type: string;
      description: string;
      affectedEntries: string[];
    }>;
  }> {
    const issues: Array<{
      type: string;
      description: string;
      affectedEntries: string[];
    }> = [];

    // Check for duplicate entries
    const duplicates = new Map<string, string[]>();
    for (const [key, entry] of this.prices.entries()) {
      const duplicateKey = `${entry.propertyId}-${entry.roomTypeId}-${entry.date}`;
      if (!duplicates.has(duplicateKey)) {
        duplicates.set(duplicateKey, []);
      }
      duplicates.get(duplicateKey)!.push(key);
    }

    for (const [duplicateKey, keys] of duplicates.entries()) {
      if (keys.length > 1) {
        issues.push({
          type: 'DUPLICATE_ENTRIES',
          description: `Multiple price entries found for ${duplicateKey}`,
          affectedEntries: keys,
        });
      }
    }

    // Check for invalid prices
    const invalidPrices: string[] = [];
    for (const [key, entry] of this.prices.entries()) {
      const validationErrors = this.validatePrice(entry.price, entry.currency);
      if (validationErrors.length > 0) {
        invalidPrices.push(key);
      }
    }

    if (invalidPrices.length > 0) {
      issues.push({
        type: 'INVALID_PRICES',
        description: 'Entries with invalid price values',
        affectedEntries: invalidPrices,
      });
    }

    // Check for missing required fields
    const missingFields: string[] = [];
    for (const [key, entry] of this.prices.entries()) {
      if (
        !entry.propertyId ||
        !entry.roomTypeId ||
        !entry.date ||
        !entry.createdBy
      ) {
        missingFields.push(key);
      }
    }

    if (missingFields.length > 0) {
      issues.push({
        type: 'MISSING_FIELDS',
        description: 'Entries with missing required fields',
        affectedEntries: missingFields,
      });
    }

    // Check Supabase data integrity
    try {
      const { data: invalidRates, error } = await supabase
        .from('current_room_rates')
        .select('property_id, room_type_id, date, rate')
        .or('rate.is.null,rate.lt.0');

      if (error) {
        throw error;
      }

      if (invalidRates && invalidRates.length > 0) {
        issues.push({
          type: 'SUPABASE_INVALID_RATES',
          description: 'Invalid rates found in Supabase database',
          affectedEntries: invalidRates.map(
            (r) => `${r.property_id}-${r.room_type_id}-${r.date}`
          ),
        });
      }
    } catch (error) {
      log.errorWithStack(
        error instanceof Error
          ? error
          : new Error('Error checking Supabase data integrity'),
        'priceStorage',
        { operation: 'verifyDataIntegrity' }
      );
      issues.push({
        type: 'SUPABASE_CONNECTION_ERROR',
        description: 'Failed to verify Supabase data integrity',
        affectedEntries: [],
      });
    }

    return {
      isValid: issues.length === 0,
      issues,
    };
  }

  // 7. ERROR RECOVERY
  async recoverFromErrors(): Promise<{
    recovered: boolean;
    actions: string[];
  }> {
    const actions: string[] = [];

    try {
      // Check data integrity
      const integrity = await this.verifyDataIntegrity();

      if (!integrity.isValid) {
        // Remove duplicate entries (keep the latest version)
        for (const issue of integrity.issues) {
          if (issue.type === 'DUPLICATE_ENTRIES') {
            const entries = issue.affectedEntries
              .map((key) => ({
                key,
                entry: this.prices.get(key)!,
              }))
              .sort((a, b) => b.entry.version - a.entry.version);

            // Keep the highest version, remove others
            for (let i = 1; i < entries.length; i++) {
              this.prices.delete(entries[i].key);
              actions.push(`Removed duplicate entry: ${entries[i].key}`);
            }
          }

          if (issue.type === 'INVALID_PRICES') {
            for (const key of issue.affectedEntries) {
              this.prices.delete(key);
              actions.push(`Removed invalid price entry: ${key}`);
            }
          }

          if (issue.type === 'MISSING_FIELDS') {
            for (const key of issue.affectedEntries) {
              this.prices.delete(key);
              actions.push(`Removed incomplete entry: ${key}`);
            }
          }
        }

        // Persist cleaned data
        await this.persistToStorage();
        actions.push('Persisted cleaned data to storage');

        // Sync with Supabase
        await this.syncWithSupabase();
        actions.push('Synced cleaned data with Supabase');
      }

      return {
        recovered: true,
        actions,
      };
    } catch (error) {
      log.errorWithStack(
        error instanceof Error ? error : new Error('Error during recovery'),
        'priceStorage',
        { operation: 'recoverFromErrors' }
      );
      return {
        recovered: false,
        actions: [
          ...actions,
          `Recovery failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        ],
      };
    }
  }

  // Sync local cache with Supabase
  private async syncWithSupabase(): Promise<void> {
    try {
      // Get all entries from local cache
      const entries = Array.from(this.prices.values());

      // Prepare batch upsert data
      const currentRates = entries.map((entry) => ({
        property_id: entry.propertyId,
        room_type_id: entry.roomTypeId,
        date: entry.date,
        rate: entry.price,
        currency: entry.currency,
        last_updated: new Date().toISOString(),
        updated_by: entry.updatedBy || entry.createdBy,
      }));

      // Batch upsert to current_room_rates
      if (currentRates.length > 0) {
        const { error } = await supabase
          .from('current_room_rates')
          .upsert(currentRates, {
            onConflict: 'property_id,room_type_id,date,rate_plan',
          });

        if (error) {
          throw error;
        }
      }
    } catch (error) {
      log.errorWithStack(
        error instanceof Error
          ? error
          : new Error('Failed to sync with Supabase'),
        'priceStorage',
        { operation: 'syncWithSupabase' }
      );
      throw error;
    }
  }

  // 8. EXPORT/IMPORT
  async exportPrices(filters?: {
    propertyId?: string;
    roomTypeId?: string;
    dateRange?: { start: string; end: string };
  }): Promise<string> {
    const prices = await this.getPrices(filters || {});

    const exportData = {
      metadata: {
        exportDate: new Date().toISOString(),
        totalEntries: prices.length,
        filters: filters || {},
      },
      prices: prices,
    };

    return JSON.stringify(exportData, null, 2);
  }

  async importPrices(
    jsonData: string,
    options: {
      overwrite?: boolean;
      validateBeforeImport?: boolean;
    } = {}
  ): Promise<{
    success: boolean;
    imported: number;
    skipped: number;
    errors: string[];
  }> {
    const { overwrite = false, validateBeforeImport = true } = options;
    const errors: string[] = [];
    let imported = 0;
    let skipped = 0;

    try {
      const data = JSON.parse(jsonData);

      if (!data.prices || !Array.isArray(data.prices)) {
        throw new Error('Invalid import format: missing prices array');
      }

      for (const priceData of data.prices) {
        try {
          if (validateBeforeImport) {
            const validationErrors = this.validatePrice(
              priceData.price,
              priceData.currency
            );
            if (validationErrors.length > 0) {
              errors.push(
                `Validation failed for ${priceData.propertyId}-${priceData.roomTypeId}-${priceData.date}: ${validationErrors.map((e) => e.error).join(', ')}`
              );
              skipped++;
              continue;
            }
          }

          const key = `${priceData.propertyId}-${priceData.roomTypeId}-${priceData.date}`;

          if (!overwrite && this.prices.has(key)) {
            skipped++;
            continue;
          }

          const result = await this.savePrice({
            propertyId: priceData.propertyId,
            roomTypeId: priceData.roomTypeId,
            date: priceData.date,
            price: priceData.price,
            currency: priceData.currency,
            createdBy: priceData.createdBy || 'import',
            adjustmentReason: 'Imported from external source',
          });

          if (result.success) {
            imported++;
          } else {
            errors.push(
              `Failed to import ${key}: ${result.errors?.map((e) => e.error).join(', ')}`
            );
            skipped++;
          }
        } catch (error) {
          errors.push(
            `Error processing entry: ${error instanceof Error ? error.message : 'Unknown error'}`
          );
          skipped++;
        }
      }

      return {
        success: errors.length === 0,
        imported,
        skipped,
        errors,
      };
    } catch (error) {
      return {
        success: false,
        imported: 0,
        skipped: 0,
        errors: [
          error instanceof Error
            ? error.message
            : 'Unknown error during import',
        ],
      };
    }
  }
}
